import { ChangeEvent, useContext, useEffect, useState } from 'react'
import { Select } from '@/components/Select'
import { Button } from '@/components/ui/button'
import classNames from 'classnames'
import { Box, FlexBox } from 'components/Box'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import styles from './Pager.module.scss'

interface PagerProps {
  namespace?: string
  take: number
  skip: number
  page: number
  pageSize: number

  onPageChange(page: number): void
  onPageSizeChange(pageSize: number): void
  onSkipCountChange?: (skipCount: boolean) => void
}

interface PagerNamespaceProps {
  namespace?: string
}

export const usePager = (
  props: PagerNamespaceProps
): {
  sessionFilterName: string
  sessionFilterPageSize: number
} => {
  const { namespace = '' } = props

  const filterName = `itemsPerPage${namespace}`
  const filterPageSize = sessionStorage.getItem(filterName)

  return {
    sessionFilterName: filterName,
    sessionFilterPageSize: filterPageSize ? Number.parseInt(filterPageSize) : 0,
  }
}

export const Pager: React.FC<PagerProps> = (props) => {
  const { page, pageSize, onPageChange, onPageSizeChange, onSkipCountChange } = props

  const { sessionFilterName: filterName, sessionFilterPageSize } = usePager(props)

  const [tableData] = useContext(TableStateContext)
  const [isLastPage, setIsLastPage] = useState<boolean>(true)

  useEffect(() => {
    if (checkIsPageLast(tableData, page)) setIsLastPage(true)
    else setIsLastPage(false)
  }, [tableData, page])

  useEffect(() => {
    if (!sessionFilterPageSize) sessionStorage.setItem(filterName, pageSize.toString())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target
    if (onSkipCountChange) {
      onSkipCountChange(checked)
    }
  }

  const setPageChange = async (exactPage: undefined | number, offset: number) => {
    if (exactPage !== undefined) return onPageChange(exactPage)
    const pageWithOffset = page + offset

    // prevent wrong lastPage change
    if (
      offset !== -1 &&
      tableData?.skipCount &&
      tableData.meta &&
      tableData?.meta?.itemsCount < tableData?.meta?.pageSize
    ) {
      return
    }

    // prevent change the page if the next one should not exists
    if (!tableData?.skipCount && tableData?.meta && pageWithOffset > tableData?.meta?.totalPages) {
      return
    }

    onPageChange(pageWithOffset)
  }

  const pageSizeChanger = (value: number) => {
    sessionStorage.setItem(filterName, value.toString())
    onPageSizeChange(value)
  }

  return (
    <Box
      className={classNames('k-pager-wrap k-pager k-widget k-grid-pager', styles.pager)}
      display='flex'
      flexDirection='row'
    >
      <FlexBox alignItems='center' justifyContent='flex-start' flex={1}>
        <Button onClick={() => setPageChange(1, 0)} disabled={page < 2} variant='ghost' size='icon'>
          <ChevronsLeft />
        </Button>
        <Button
          onClick={() => setPageChange(undefined, -1)}
          disabled={page < 2}
          variant='ghost'
          size='icon'
        >
          <ChevronLeft />
        </Button>
        <span className={styles.page}>
          {(page - 1) * pageSize + 1} -{' '}
          {tableData?.meta?.itemsCount
            ? (page - 1) * pageSize + tableData.meta.itemsCount
            : page * pageSize}
        </span>
        <Button
          onClick={() => setPageChange(undefined, 1)}
          disabled={isLastPage}
          variant='ghost'
          size='icon'
        >
          <ChevronRight />
        </Button>
        {!tableData?.skipCount && (
          <Button
            onClick={() => setPageChange(tableData?.meta?.totalPages, 1)}
            disabled={isLastPage}
            variant='ghost'
            size='icon'
          >
            <ChevronsRight />
          </Button>
        )}
        <div className='flex gap-1 items-center'>
          <Select
            size='sm'
            className='max-w-18'
            options={[10, 20, 50, 100].map((v) => ({ value: v }))}
            onChange={pageSizeChanger}
            value={sessionFilterPageSize || pageSize}
          />
          <span className={styles.pageSizeText}>items per page</span>
        </div>
      </FlexBox>
      <FlexBox className={styles.skipCount}>
        {!tableData?.skipCount && (
          <span>
            {(tableData?.meta?.totalResults ?? Infinity) <= page * pageSize
              ? `${(page - 1) * pageSize + 1} - ${tableData?.meta?.totalResults} of ${
                  tableData?.meta?.totalResults
                } items`
              : `${(page - 1) * pageSize + 1} - ${page * pageSize} of ${
                  tableData?.meta?.totalResults
                } items`}
          </span>
        )}
        {!tableData?.skipCount && <span className={classNames(styles.dividerRight)} />}
        <span>Skip count:</span>
        <input type='checkbox' checked={tableData?.skipCount} onChange={onChange} />
      </FlexBox>
    </Box>
  )
}

const checkIsPageLast = (tableData: TableStateContextData, page: number) => {
  if (tableData?.meta?.pageIndex !== page) return true
  if (!tableData?.skipCount) return tableData?.meta && page + 1 > tableData?.meta?.totalPages
  if (tableData?.skipCount)
    return tableData?.meta && tableData?.meta?.itemsCount < tableData?.meta?.pageSize
}
